/**
 * Partner Credit Packs API client (Phase 4′ / Phase 9).
 * Fetches available credit pack products from GET /v1/credit-packs.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';

export interface CreditPackView {
  id: string;
  code: string;
  displayName: string;
  amountPaise: number;
  credits: number;
  currency: string;
  /** PUBLISH = legacy publish-count pack; VALUE = rupee wallet, 1 credit = ₹1 (ADR-015). */
  unit: 'PUBLISH' | 'VALUE';
  /** "N% off" — the pack ratio, the only partner discount (ADR-029). */
  discountPercent: number;
  displayOrder: number;
  isActive: boolean;
  grossPricePerCredit: number;
  netPricePerCredit: number;
  createdAt: string;
  updatedAt: string;
}

export async function fetchActiveCreditPacks(): Promise<CreditPackView[]> {
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  const res = await fetch(`${API_BASE}/credit-packs`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as CreditPackView[];
}

export interface WalletSummary {
  /** VALUE wallet balance in ₹ (1 credit = ₹1). */
  balance: number;
  /** Blended "N% off" over purchased lots (ADR-029); null when none held. */
  blendedDiscountPercent: number | null;
}

export async function fetchWallet(): Promise<WalletSummary> {
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  const res = await fetch(`${API_BASE}/me/credits/wallet`, {
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as WalletSummary;
}
